import React, { useMemo, useState } from 'react';

import { IconButton, Tooltip, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import NotificationsIcon from '@material-ui/icons/Notifications';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import { format, isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { generatePath } from 'react-router-dom';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import InputWithDebouncedAction from 'components/InputWithDebouncedAction/InputWithDebouncedAction';
import Table from 'components/Table';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import { ActionLibraryInstance } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import ROLES from 'config/constants/ROLES';
import { translatedOrFallback } from 'helpers/safeT/safeT';
import usePermissions from 'hooks/usePermissions';
import action_library_messages from 'messages/action_library_messages';
import assets_messages from 'messages/assets_messages';
import search_messages from 'messages/search_messages';
import DelegateInstanceDialog from 'pages/ActionLibraryInstance/ActionLibraryInstanceDetailPage/_components/DelegateInstanceDialog/DelegateInstanceDialog';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';
import { TableDataMappingRow } from 'types/Table';

import useStyles from './ActionLibraryInstancesTable.styles';

type Props = {
  data: ActionLibraryInstance[] | null;
  loading: boolean;
  sort: { key: string | null; direction: string | null };
  onSort: (key: string, direction: string) => void;
  filtersService?: any;
  onSearch?: (searchTerm: string) => void;
  searchValue?: string;
  searchAdornment?: React.ReactElement | null;
};

type InstanceActionsCellProps = {
  instance: ActionLibraryInstance;
  onOpenDelegate: (instance: ActionLibraryInstance) => void;
};

type ValidityTableCellProps = { instance: ActionLibraryInstance };

const ValidityTableCell: React.FC<ValidityTableCellProps> = ({ instance }) => {
  const { t } = useTranslation();

  const tooltipTitle =
    instance.resultValidityPeriodMonths != null
      ? t(action_library_messages.table.validity_column.tooltip_valid_for_months, {
          months: instance.resultValidityPeriodMonths,
        })
      : t(action_library_messages.table.validity_column.tooltip_valid_for_na);

  let content: string;
  if (instance.validUntil) {
    content = format(new Date(instance.validUntil), 'yyyy-MM-dd');
  } else if (instance.resultValidityPeriodMonths != null) {
    content = t(action_library_messages.table.instance_status.in_progress);
  } else {
    content = t(action_library_messages.table.validity_column.not_defined);
  }

  return (
    <Tooltip title={tooltipTitle}>
      <span style={{ whiteSpace: 'nowrap' }}>{content}</span>
    </Tooltip>
  );
};

const InstanceActionsCell: React.FC<InstanceActionsCellProps> = ({ instance, onOpenDelegate }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showConfirmationModal } = useConfirmationModalContext();
  const [isOrgAssetAdmin] = usePermissions([ROLES.ORGANIZATION.ASSETS_MANAGER]);
  const { [ROLES.USER_DATA_FLAGS.IS_ASSET_OWNER]: isAssetOwner } = userDataStorage.get() || {};

  const canDelete = instance.canDelete === true;
  const canDelegate = (isOrgAssetAdmin || isAssetOwner) && instance.status !== 'completed';

  const { mutate: deleteInstance } = useMutation(actionLibraryInstancesApi.deleteActionLibraryInstance, {
    onSuccess: () => {
      queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL');
    },
  });

  const handleDelegate = () => {
    onOpenDelegate(instance);
  };

  const handleDelete = async () => {
    // @ts-ignore
    const title = t(action_library_messages.delete_confirmation.title);
    // @ts-ignore
    const body = t(action_library_messages.delete_confirmation.body);

    const confirmed = await showConfirmationModal({
      title,
      body,
    });

    if (confirmed) {
      deleteInstance(instance.id);
    }
  };

  const menuElements = [
    ...(canDelegate
      ? [
          {
            disabled: instance.status === 'completed',
            label: t(action_library_messages.instance_actions.delegate),
            onClick: handleDelegate,
            icon: <PeopleOutlinedIcon fontSize='small' />,
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            disabled: instance.status === 'completed',
            icon: <DeleteOutlinedIcon fontSize='small' />,
            label: t(action_library_messages.actions.delete),
            onClick: handleDelete,
          },
        ]
      : []),
  ];

  return (
    <ContextMenu elements={menuElements} id={`action-instance-${instance.id}`}>
      <IconButton size='small'>
        <MoreIcon />
      </IconButton>
    </ContextMenu>
  );
};

const ActionLibraryInstancesTable: React.FC<Props> = ({
  data,
  loading,
  sort,
  onSort,
  filtersService,
  onSearch,
  searchValue,
  searchAdornment,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [delegateInstance, setDelegateInstance] = useState<ActionLibraryInstance | null>(null);

  const parsedData = useMemo(() => {
    if (!data) return null;
    return data;
  }, [data]);

  const getRowHref = (instance: ActionLibraryInstance) => generatePath(PATHS.ACTION_LIBRARY_INSTANCE_DETAIL, { id: instance.id });

  const dataMapping: TableDataMappingRow<ActionLibraryInstance>[] = useMemo(
    () => [
      {
        id: 'title',
        width: '14%',
        label: t(action_library_messages.table.name),
        get: (instance: ActionLibraryInstance) => instance.title,
      },
      {
        id: 'area',
        width: '7%',
        label: t(action_library_messages.table.area),
        get: (instance: ActionLibraryInstance) => {
          const { areaData } = instance;
          if (areaData?.displayName) return areaData.displayName;
          if (areaData?.displayNameTranslationKey) {
            return translatedOrFallback(t, areaData.displayNameTranslationKey, areaData.displayName ?? areaData.name);
          }
          return instance.areaDisplay ?? (instance.area != null ? String(instance.area) : '-');
        },
      },
      {
        id: 'subArea',
        width: '7%',
        label: t(action_library_messages.table.subarea),
        get: (instance: ActionLibraryInstance) => {
          const { subAreaData } = instance;
          if (!subAreaData) {
            return instance.subAreaDisplay ?? (instance.subArea != null ? String(instance.subArea) : '-');
          }
          if (subAreaData.nameTranslationKey) {
            return translatedOrFallback(t, subAreaData.nameTranslationKey, subAreaData.name);
          }
          return (subAreaData.name || instance.subAreaDisplay) ?? (instance.subArea != null ? String(instance.subArea) : '-');
        },
      },
      {
        id: 'assetName',
        width: '10%',
        label: t(action_library_messages.instance.detail.asset_name),
        get: (instance: ActionLibraryInstance) => instance.assetName ?? '-',
      },
      {
        id: 'assetType',
        width: '15%',
        label: t(action_library_messages.instance.detail.asset_type),
        get: (instance: ActionLibraryInstance) => instance.assetType ?? '-',
      },
      {
        id: 'assetImportance',
        width: '10%',
        label: t(assets_messages.information_label.assets_importance),
        get: (instance: ActionLibraryInstance) => {
          if (!instance.assetImportance) return '-';
          // @ts-ignore
          return t(assets_messages.information_choices_assets_importance[instance.assetImportance]) || instance.assetImportance;
        },
      },
      {
        id: 'criticality',
        width: '5%',
        label: t(action_library_messages.table.criticality),
        get: (instance: ActionLibraryInstance) => {
          const { criticality } = instance;
          const criticalityKey = criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';
          let IconComponent;
          let iconClass;

          if (criticality === 'low') {
            IconComponent = InfoIcon;
            iconClass = styles.criticalityLowIcon;
          } else if (criticality === 'medium') {
            IconComponent = WarningIcon;
            iconClass = styles.criticalityMediumIcon;
          } else {
            IconComponent = ErrorIcon;
            iconClass = styles.criticalityHighIcon;
          }

          return (
            <div className={styles.statusCell}>
              <IconComponent className={`${styles.statusIcon} ${iconClass}`} />
              <span>{t(action_library_messages.criticality[criticalityKey])}</span>
            </div>
          );
        },
      },
      {
        id: 'deadline',
        width: '9%',
        label: t(action_library_messages.table.due_date),
        get: (instance: ActionLibraryInstance) => {
          const deadlineDate = instance.deadline ? new Date(instance.deadline) : null;
          const reminderDate = instance.reminder ? new Date(instance.reminder) : null;

          const isDeadlinePast = deadlineDate && isPast(deadlineDate) && instance.status !== 'completed';
          const isReminderPast = reminderDate && isPast(reminderDate) && instance.status !== 'completed' && !isDeadlinePast;

          let dateColor: string | undefined;
          let iconColor: string;
          if (isDeadlinePast) {
            dateColor = '#f44336';
            iconColor = '#f44336';
          } else if (isReminderPast) {
            dateColor = '#ed6c02';
            iconColor = '#ed6c02';
          } else {
            iconColor = 'rgba(0, 0, 0, 0.54)';
          }

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {reminderDate && (
                  <Tooltip title={`${t(action_library_messages.instance.form.reminder)}: ${format(reminderDate, 'yyyy-MM-dd')}`}>
                    <NotificationsIcon
                      style={{
                        fontSize: '1.1rem',
                        marginRight: 8,
                        color: iconColor,
                      }}
                    />
                  </Tooltip>
                )}
                {deadlineDate ? (
                  <Typography
                    style={{
                      color: dateColor ?? 'inherit',
                      fontWeight: isDeadlinePast ? 'bold' : 'normal',
                    }}
                    variant='body2'
                  >
                    {format(deadlineDate, 'yyyy-MM-dd')}
                  </Typography>
                ) : (
                  <Typography variant='body2'>-</Typography>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: 'status',
        width: '8%',
        label: t(action_library_messages.table.status),
        get: (instance: ActionLibraryInstance) => {
          let IconComponent;
          let iconClass;
          const { status, statusPercent } = instance;
          const statusKey = status ?? 'not_started';
          const statusMessage =
            action_library_messages.table.instance_status[statusKey] ?? action_library_messages.table.instance_status.not_started;
          const statusLabel = t(statusMessage);

          if (status === 'completed') {
            IconComponent = CheckCircleIcon;
            iconClass = styles.activeIcon;
          } else if (status === 'in_progress') {
            IconComponent = RemoveCircleOutlineIcon;
            iconClass = styles.criticalityMediumIcon;
          } else {
            IconComponent = CancelIcon;
            iconClass = styles.inactiveIcon;
          }

          return (
            <div className={styles.statusCell}>
              <IconComponent className={`${styles.statusIcon} ${iconClass}`} />
              <span>
                {statusLabel}
                {typeof statusPercent === 'number' && status !== 'completed' ? ` (${statusPercent}%)` : ''}
              </span>
            </div>
          );
        },
      },
      {
        id: 'owner',
        width: '12%',
        label: t(action_library_messages.table.responsible_user),
        get: (instance: ActionLibraryInstance) => instance.delegatedToFullName || instance.ownerFullName || '-',
      },
      {
        id: 'createdAt',
        width: '8%',
        label: t(action_library_messages.table.instance_creation_date),
        get: (instance: ActionLibraryInstance) => {
          const value = instance.createdAt ? format(new Date(instance.createdAt), 'yyyy-MM-dd') : '-';
          return <span style={{ whiteSpace: 'nowrap' }}>{value}</span>;
        },
      },
      {
        id: 'validity',
        width: '16%',
        label: t(action_library_messages.table.validity),
        get: (instance: ActionLibraryInstance) => <ValidityTableCell instance={instance} />,
      },
      {
        id: 'stepsCount',
        width: '4%',
        label: t(action_library_messages.table.number_of_steps),
        get: (instance: ActionLibraryInstance) =>
          instance.stepsCount ?? instance.totalStepsCount ?? (instance.steps ? instance.steps.length : 0),
      },
      {
        id: 'reportTo',
        width: '18%',
        label: t(action_library_messages.table.report_to),
        get: (instance: ActionLibraryInstance) => <span style={{ whiteSpace: 'nowrap' }}>{instance.reportedTo ?? '-'}</span>,
      },
      {
        id: 'actions',
        width: '5%',
        blockSorting: true,
        label: '',
        get: (instance: ActionLibraryInstance) => <InstanceActionsCell instance={instance} onOpenDelegate={setDelegateInstance} />,
      },
    ],
    [t, styles],
  );

  const handleDelegateSuccess = () => {
    queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL');
    setDelegateInstance(null);
  };

  return (
    <>
      {onSearch && (
        <div className={styles.searchBarOuterWrapper}>
          <div className={styles.searchBarRow}>
            <div className={styles.searchInputCell}>
              <InputWithDebouncedAction
                action={onSearch}
                formControl={{ size: 'small' }}
                label={t(search_messages.input_label)}
                value={searchValue}
              />
            </div>
            {searchAdornment && <div className={styles.searchAdornmentCell}>{searchAdornment}</div>}
          </div>
        </div>
      )}
      <div className={styles.tableScrollWrapper}>
        <Table
          clickableRow
          data={parsedData}
          dataMapping={dataMapping}
          enableSearch={false}
          filterInLastColumn={!!filtersService}
          filtersService={filtersService}
          getRowHref={getRowHref}
          onSort={onSort}
          refetching={loading}
          sort={sort}
        />
      </div>
      {delegateInstance && (
        <DelegateInstanceDialog
          instance={delegateInstance}
          onClose={() => setDelegateInstance(null)}
          onSuccess={handleDelegateSuccess}
          open
        />
      )}
    </>
  );
};

export default ActionLibraryInstancesTable;
